<template>
  <div class="leave-list">
    <van-nav-bar
      :title="childName + '的请假管理'"
      left-arrow
      @click-left="goBack"
      fixed
      placeholder
    />

    <!-- 家长须知（与小程序端文案一致） -->
    <div class="notice-box">
      <div class="notice-line">
        · 请假审核通过后，家长不能取消请假，如需取消请联系班主任。
      </div>
      <div class="notice-line">
        · 请假功能用于辅助老师进行考勤签到，学生的出勤数据以真实出勤情况为准。
      </div>
    </div>

    <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
      <van-list
        v-model:loading="loading"
        :finished="finished"
        finished-text="没有更多了"
        @load="loadData"
        :immediate-check="false"
      >
        <div v-if="list.length > 0" class="list-wrap">
          <div class="leave-card" v-for="item in list" :key="item.id">
            <div class="card-header">
              <div class="leave-dates-wrap">
                <span
                  class="leave-date-tag"
                  v-for="d in parseDates(item.dates)"
                  :key="d"
                  >{{ d }}</span
                >
                <span class="leave-date-count" v-if="parseDates(item.dates).length">
                  共{{ parseDates(item.dates).length }}天
                </span>
              </div>
              <div class="header-right">
                <van-tag :type="leaveStatusType(item.status)" plain size="medium">
                  {{ leaveStatusText(item.status) }}
                </van-tag>
                <!-- 只有「待审核」(1) 给取消入口：已通过(2) 按顶部须知不允许家长取消，
                     已取消/已驳回(3)、历史已取消(4) 本身也不该再出现取消按钮 -->
                <van-button
                  v-if="item.status == 1"
                  size="mini"
                  type="danger"
                  plain
                  class="cancel-btn"
                  @click="handleCancelLeave(item)"
                  >取消请假</van-button
                >
              </div>
            </div>
            <div class="card-body">
              <div class="info-row">
                <span class="label">班级：</span>
                <span>{{ item.class_name || "-" }}</span>
              </div>
              <div class="info-row">
                <span class="label">原因：</span>
                <span>{{ item.content || "-" }}</span>
              </div>
              <div class="info-row time">
                <span>提交时间：{{ item.create_at }}</span>
              </div>
            </div>
          </div>
        </div>
        <van-empty v-else-if="!loading" description="暂无请假记录" />
      </van-list>
    </van-pull-refresh>

    <div class="bottom-action">
      <van-button type="primary" block round @click="toLeaveApply"> 去请假 </van-button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { Toast, Dialog } from "vant";
import api from "../service/api";
import { leaveStatusText, leaveStatusType } from "../utils/leaveStatus";

export default {
  setup() {
    const router = useRouter();
    const childName = ref(sessionStorage.getItem("childName") || "孩子");
    const list = ref([]);
    const loading = ref(false);
    const finished = ref(false);
    const refreshing = ref(false);
    const page = ref(1);

    // 解析日期字符串为数组
    const parseDates = (dates) => {
      if (!dates) return [];
      return dates
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean);
    };

    // 状态文案/标签类型见 ../utils/leaveStatus（共用，避免三处漂移）

    // 加载数据
    const loadData = async () => {
      try {
        const res = await api.getLeaveListByUserId({
          sid: localStorage.getItem("sid"),
          user_id: sessionStorage.getItem("userId"),
          page: page.value,
        });
        loading.value = false;
        if (res.data.code == 1001) {
          const result = res.data.result;
          if (refreshing.value) {
            list.value = [];
            refreshing.value = false;
          }
          list.value = list.value.concat(result.data || []);
          if (result.current_page >= result.last_page) {
            finished.value = true;
          } else {
            page.value++;
          }
        } else {
          finished.value = true;
          Toast(res.data.message || "加载失败");
        }
      } catch (error) {
        loading.value = false;
        refreshing.value = false;
        Toast("加载失败");
      }
    };

    // 下拉刷新
    const onRefresh = () => {
      page.value = 1;
      finished.value = false;
      loading.value = true;
      loadData();
    };

    const toLeaveApply = () => {
      router.push({ path: "/leaveApply" });
    };
    const goBack = () => router.back();

    // 家长端取消请假：role=1 告知后端这是家长端调用；
    // 后端 recordLeave 已对 role=1 加拦截（已审核通过则拒绝），与教师端分开
    const handleCancelLeave = async (item) => {
      try {
        await Dialog.confirm({
          title: "取消请假",
          message: `确定取消 ${item.student_name || childName.value} 的请假申请吗？`,
        });
      } catch (e) {
        return; // 用户取消确认
      }
      // forbidClick 故意关掉：遮罩会挡住左上角返回；防重复由请求层白名单兜底
      Toast.loading({ message: "处理中...", forbidClick: false });
      try {
        const res = await api.recordLeave({
          sid: localStorage.getItem("sid"),
          user_id: sessionStorage.getItem("userId"),
          student_leave_id: item.id,
          content: "",
          status: 3,
          role: 1,
        });
        if (res.data.code == 1001) {
          Toast("已取消请假");
          // 仅更新本地状态：status 置 3 → 按钮随 v-if 消失，文案显示"已取消"
          const idx = list.value.findIndex((it) => it.id === item.id);
          if (idx > -1) {
            list.value[idx] = { ...list.value[idx], status: 3 };
          }
        } else {
          Toast(res.data.message || "操作失败");
        }
      } catch (e) {
        Toast("网络请求失败");
      }
    };

    onMounted(() => {
      loading.value = true;
      loadData();
    });

    return {
      childName,
      list,
      loading,
      finished,
      refreshing,
      parseDates,
      leaveStatusText,
      leaveStatusType,
      loadData,
      onRefresh,
      toLeaveApply,
      goBack,
      handleCancelLeave,
    };
  },
};
</script>

<style lang="less" scoped>
.leave-list {
  min-height: 100vh;
  background: #f2f2f2;
  padding-bottom: 80px;
}

.list-wrap {
  padding: 10px;
}

.leave-card {
  background: #fff;
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 10px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f5f5f5;
}

.leave-dates-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  flex: 1;
  margin-right: 8px;
}

.leave-date-tag {
  display: inline-block;
  padding: 3px 10px;
  background: var(--color-primary-light);
  color: var(--color-primary);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.leave-date-count {
  font-size: 12px;
  color: #999;
}

.card-body {
  font-size: 13px;
  color: #666;
}

.info-row {
  margin-bottom: 6px;

  .label {
    color: #999;
  }
}

.info-row.time {
  color: #bbb;
  font-size: 12px;
  margin-top: 8px;
  margin-bottom: 0;
}

.bottom-action {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 15px;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

.notice-box {
  margin: 10px;
  padding: 10px 12px;
  background: #fff7e6;
  border: 1px solid #ffe7ba;
  border-radius: 8px;

  .notice-line {
    font-size: 12px;
    color: #a06b1f;
    line-height: 20px;
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cancel-btn {
  margin-left: 2px;
}
</style>
